import React, { FC, useMemo } from "react";
import { Field, useForm } from "react-final-form";
import clsx from "clsx";
import { Theme } from "@material-ui/core/styles";
import InputLabel from "@material-ui/core/InputLabel";
import makeStyles from "@material-ui/core/styles/makeStyles";
import Typography from "@material-ui/core/Typography";
import { Grid } from "@material-ui/core";
import WeduCheckbox from "../../../../../components/WeduCheckbox/WeduCheckbox";
import WeduTooltip from "../../../../../components/WeduTooltip/intex";
import { COURSE_REQUIRED_DOCS } from "../../../constants";
import FieldsGroupLabel from "../common/FieldsGroupLabel";
import { FieldArrayRenderProps } from "react-final-form-arrays";

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    paddingBottom: 3,
  },
  headerContainerError: {
    borderTopColor: `${theme.palette.error.main} !important`,
    borderLeftColor: `${theme.palette.error.main} !important`,
    borderRightColor: `${theme.palette.error.main} !important`,
  },
  checkboxContainerError: {
    borderBottomColor: `${theme.palette.error.main} !important`,
    borderLeftColor: `${theme.palette.error.main} !important`,
    borderRightColor: `${theme.palette.error.main} !important`,
  },
  headerContainer: {
    border: "solid 1px #dbdbdb",
    borderTopLeftRadius: 15,
    borderTopRightRadius: 15,
    height: 44,
    padding: "0 16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  checkboxContainer: {
    borderLeft: "solid 1px #dbdbdb",
    borderRight: "solid 1px #dbdbdb",
    borderBottom: "solid 1px #dbdbdb",
    backgroundColor: "#f5f6f7",
    borderBottomLeftRadius: 15,
    borderBottomRightRadius: 15,
    padding: "8px 0 8px 16px",
  },
  span: {
    fontFamily: "Roboto",
    fontSize: 14,
    fontWeight: "normal",
    color: "#bcbcbc",
  },
}));

interface IRequiredDocsMultiSelectProps
  extends FieldArrayRenderProps<any, HTMLElement> {
  infoText: string;
  errors: { [key: string]: string };
}

const RequiredDocsMultiSelect: FC<IRequiredDocsMultiSelectProps> = ({
  fields: { value = [], name },
  infoText,
  errors,
}) => {
  const classes = useStyles();

  const { change } = useForm();

  const hasError = !!errors.requiredDocs;

  // TODO: check receivedMainDoc???
  const mergedRequiredDocs = useMemo(
    () =>
      COURSE_REQUIRED_DOCS.map((mainDoc) => {
        const receivedMainDoc = value.find(
          (receivedDoc) => receivedDoc.name === mainDoc.name,
        );

        return receivedMainDoc || mainDoc;
      }),
    [],
  );

  const selectedDocsCount = value?.length || 0;
  const allDocsCount = COURSE_REQUIRED_DOCS.length;

  const selectAllDeselectAllHandler = () => {
    if (selectedDocsCount === allDocsCount) {
      change(name, []);
      return;
    }

    change(name, mergedRequiredDocs);
  };

  return (
    <>
      <FieldsGroupLabel
        isRequired
        label="Required Documents"
        hasError={hasError}
        infoText={infoText}
        className="fields-group-label-first"
      />
      <div className={classes.root}>
        <div
          className={clsx(
            classes.headerContainer,
            hasError && classes.headerContainerError,
          )}
        >
          <span className={classes.span}>
            {selectedDocsCount
              ? `${selectedDocsCount} of ${allDocsCount} Selected`
              : "Nothing  Selected"}
          </span>
          <Typography
            className={"wedu-link"}
            onClick={selectAllDeselectAllHandler}
          >
            {selectedDocsCount === allDocsCount ? "Deselect All" : "Select All"}
          </Typography>
        </div>
        <div
          className={clsx(
            classes.checkboxContainer,
            hasError && classes.checkboxContainerError,
          )}
        >
          <Grid container>
            {mergedRequiredDocs.map((doc) => (
              <Grid key={doc.name} item xs={12} sm={3}>
                {doc.longName === doc.shortName ? (
                  <Field
                    value={doc}
                    label={doc.shortName}
                    type="checkbox"
                    name={name}
                    component={WeduCheckbox}
                  />
                ) : (
                  <WeduTooltip title={doc.longName}>
                    <div>
                      <Field
                        value={doc}
                        label={doc.shortName}
                        type="checkbox"
                        name={name}
                        component={WeduCheckbox}
                      />
                    </div>
                  </WeduTooltip>
                )}
              </Grid>
            ))}
          </Grid>
        </div>
      </div>
      {hasError && (
        <InputLabel className="field-error-text">
          {errors.requiredDocs}
        </InputLabel>
      )}
    </>
  );
};

export default RequiredDocsMultiSelect;
